import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import "dayjs/locale/ar-sa";
import hijriMoment from "moment-hijri";
import { Box, IconButton, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";
import { useSelector } from "react-redux";
import { CalendarRange, X } from "lucide-react";
import { UIFieldShell, useUIField } from "../form";
import { clx } from "../../../utils/Helpers";
import AdapterMomentHijri from "../../../utils/dateAdapters/AdapterMomentHijri";

const CalendarRangeIcon = () => (
  <CalendarRange size={24} color="var(--color-foreground-subtle, #94A3B8)" />
);

/**
 * `moment-hijri`'s Umm al-Qura conversion table only covers Hijri years 1356–1500
 * (≈ Gregorian 1937–2077). MUI's own default picker bounds are Gregorian 1900–2099,
 * which falls outside that table on both ends — `getYearRange`/`startOf('iYear')` on
 * an out-of-table date silently returns garbage (not an error), which empties out the
 * year grid entirely. Default the Hijri picker to a safe, comfortably-inset window
 * unless the caller supplies its own `minDate`/`maxDate`.
 */
const HIJRI_SAFE_MIN_DATE = "1940-01-01";

const UIDatePicker = ({
  label,
  optionalLabel,
  required,
  helperText,
  error,
  id,
  name,
  value,
  onChange,
  onBlur,
  inputFormat,
  placeholder,
  withTime,
  disablePast,
  preventManualDateEntry,
  clearable,
  classNames,
  className,
  size,
  open,
  calendarSystem,
  ...props
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  // Set when a value is picked while the popup is open. Formik's setFieldTouched
  // validates against the pre-change values in the same tick, so validating on that
  // close could race the change's own validation and leave a stale error on screen.
  const changedWhileOpenRef = useRef(false);
  const lang = useSelector((state) => state.locale.lang);
  const fieldId = id || name;
  const field = useUIField({ name, value, error, helperText });
  const isHijri = calendarSystem === "hijri";
  // `value`/`onChange` stay a plain Gregorian ISO string either way — the calendar
  // system only changes how the picker UI navigates/displays it, not the stored value.
  const pickerValue = field.value
    ? isHijri
      ? hijriMoment(field.value).locale(lang === "ar" ? "ar-sa" : "en")
      : dayjs(field.value).locale(lang === "ar" ? "ar-sa" : "en")
    : null;
  const PickerComponent = withTime ? DesktopDateTimePicker : DesktopDatePicker;
  const resolvedInputFormat =
    inputFormat ||
    (withTime
      ? lang === "ar"
        ? "YYYY/MM/DD hh:mm A"
        : "MM/DD/YYYY, hh:mm A"
      : isHijri
      ? "iDD iMMMM. iYYYY"
      : "DD/MM/YYYY");
  const resolvedPlaceholder =
    placeholder ||
    (withTime
      ? lang === "ar"
        ? "سنة/شهر/يوم --:-- ص"
        : "mm/dd/yyyy, --:-- --"
      : lang === "ar"
      ? "يوم/شهر/سنة"
      : "DD/MM/YYYY");
  const valueFormat = withTime ? "YYYY-MM-DDTHH:mm" : "YYYY-MM-DD";
  const resolvedMinDate =
    props.minDate ?? (isHijri ? HIJRI_SAFE_MIN_DATE : undefined);
  const resolvedMaxDate =
    props.maxDate ?? (isHijri ? dayjs().format("YYYY-MM-DD") : undefined);

  const handleClear = (event) => {
    event.stopPropagation();
    field.setValue("");
    onChange?.("", null);
  };

  return (
    <UIFieldShell
      id={fieldId}
      label={label}
      optionalLabel={optionalLabel}
      required={required}
      helperText={field.helperText}
      error={field.showError}
      classNames={classNames}
    >
      <LocalizationProvider
        dateAdapter={isHijri ? AdapterMomentHijri : AdapterDayjs}
        adapterLocale={lang === "ar" ? "ar-sa" : "en"}
      >
        <PickerComponent
          {...props}
          value={pickerValue}
          minDate={resolvedMinDate}
          maxDate={resolvedMaxDate}
          inputFormat={resolvedInputFormat}
          disableMaskedInput
          disablePast={disablePast}
          open={preventManualDateEntry ? pickerOpen : open}
          components={{ OpenPickerIcon: CalendarRangeIcon }}
          onChange={(nextValue) => {
            // Normalize through a real JS Date first: `nextValue` is a dayjs instance
            // in Gregorian mode or a moment-hijri instance in Hijri mode, and only
            // `.toDate()` is guaranteed on both — the outgoing value is always the
            // same plain Gregorian ISO string regardless of calendar system.
            const jsDate = nextValue?.toDate ? nextValue.toDate() : nextValue;
            const next =
              jsDate && dayjs(jsDate).isValid()
                ? dayjs(jsDate).format(valueFormat)
                : "";
            changedWhileOpenRef.current = true;
            field.setValue(next);
            onChange?.(next, nextValue);
          }}
          onOpen={() => {
            changedWhileOpenRef.current = false;
            if (preventManualDateEntry) {
              setPickerOpen(true);
            }
          }}
          onClose={() => {
            if (preventManualDateEntry) {
              setPickerOpen(false);
            }
            field.setTouched(true, !changedWhileOpenRef.current);
            changedWhileOpenRef.current = false;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              id={fieldId}
              name={name}
              error={field.showError}
              helperText={null}
              className={clx(
                "ui-input",
                pickerValue && "ui-input--filled",
                preventManualDateEntry && "ui-input--picker-only",
                className,
              )}
              size={size}
              onClick={(event) => {
                if (preventManualDateEntry) {
                  setPickerOpen(true);
                }
                params.onClick?.(event);
              }}
              onBlur={(event) => {
                field.setTouched(true);
                onBlur?.(event);
                params.inputProps?.onBlur?.(event);
              }}
              InputProps={{
                ...params.InputProps,
                readOnly: preventManualDateEntry || params.InputProps?.readOnly,
                endAdornment: (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: lang === "ar" ? "var(--space-10)" : "",
                      flexShrink: 0,
                      marginInlineEnd: "var(--space-4)",
                    }}
                  >
                    {clearable && field.value && (
                      <IconButton
                        className="ui-date-picker__clear p-0"
                        size="small"
                        onClick={handleClear}
                        aria-label="clear"
                        tabIndex={-1}
                      >
                        <X
                          size={16}
                          color="var(--color-foreground-subtle, #94A3B8)"
                        />
                      </IconButton>
                    )}
                    {params.InputProps?.endAdornment}
                  </Box>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                placeholder: resolvedPlaceholder,
                readOnly: preventManualDateEntry || params.inputProps?.readOnly,
                onKeyDown: (event) => {
                  if (preventManualDateEntry) {
                    event.preventDefault();
                  }
                  params.inputProps?.onKeyDown?.(event);
                },
              }}
              sx={{
                "&.MuiOutlinedInput-root": {
                  overflow: "hidden",
                  // Spacing to the edge is owned by the adornment Box's own
                  // marginInlineEnd below — avoid stacking the theme's conditional
                  // endAdornment padding on top of it (was causing an LTR/RTL mismatch).
                  paddingInlineEnd: 0,
                },
                "& .MuiInputBase-input": {
                  direction: lang === "ar" ? "rtl" : "ltr",
                  textAlign: lang === "ar" ? "right" : "left",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  ...(preventManualDateEntry
                    ? {
                        cursor: "pointer",
                        caretColor: "transparent",
                      }
                    : {}),
                },
                "& .MuiInputAdornment-root": {
                  margin: 0,
                },
                "& .MuiInputAdornment-root .MuiIconButton-root": {
                  backgroundColor: "transparent",
                  padding: "4px",
                  "&:hover": { backgroundColor: "transparent" },
                  "&:focus-visible": { backgroundColor: "transparent" },
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
    </UIFieldShell>
  );
};

UIDatePicker.propTypes = {
  label: PropTypes.node,
  optionalLabel: PropTypes.node,
  required: PropTypes.bool,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  inputFormat: PropTypes.string,
  placeholder: PropTypes.string,
  withTime: PropTypes.bool,
  disablePast: PropTypes.bool,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  preventManualDateEntry: PropTypes.bool,
  clearable: PropTypes.bool,
  classNames: PropTypes.object,
  className: PropTypes.string,
  size: PropTypes.oneOf(["medium", "large"]),
  open: PropTypes.bool,
  /** "gregorian" (default) or "hijri" — which calendar the picker's navigation grid
   * (years/months/days) and formatted display use. `value`/`onChange` are always a
   * plain Gregorian ISO string either way. */
  calendarSystem: PropTypes.oneOf(["gregorian", "hijri"]),
};

UIDatePicker.defaultProps = {
  label: null,
  optionalLabel: null,
  required: false,
  helperText: null,
  error: undefined,
  id: undefined,
  name: undefined,
  value: undefined,
  onChange: undefined,
  onBlur: undefined,
  inputFormat: undefined,
  placeholder: undefined,
  withTime: false,
  disablePast: false,
  minDate: undefined,
  maxDate: undefined,
  preventManualDateEntry: false,
  clearable: false,
  classNames: {},
  className: undefined,
  size: "medium",
  calendarSystem: "gregorian",
};

export default UIDatePicker;
